import {
  Group,
  Radio as MantineRadio,
  type RadioCardProps,
  type RadioGroupProps,
  type RadioIndicatorProps,
  type RadioProps as MantineRadioProps,
  Text,
} from "@mantine/core"
import { forwardRef, type ReactNode } from "react"

import { buildLabel } from "../utils"

import radioCardClasses from "./RadioCard.module.css"

export type RadioProps = Omit<MantineRadioProps, "component"> & {
  isLoading?: boolean
}

export type RadioGroupComponentProps = RadioGroupProps

export type RadioIndicatorComponentProps = RadioIndicatorProps

export type RadioCardComponentProps = Omit<RadioCardProps, "children"> & {
  label: ReactNode
  description?: ReactNode
  children?: ReactNode
}

const RadioRoot = forwardRef<HTMLInputElement, RadioProps>(
  ({ label, isLoading, required, color = "primary", ...props }, ref) => {
    const finalLabel = buildLabel({ label, isLoading, required })

    return (
      <MantineRadio ref={ref} color={color} label={finalLabel} {...props} />
    )
  }
)

RadioRoot.displayName = "Radio"

const RadioCard = ({
  label,
  description,
  children,
  className,
  ...props
}: RadioCardComponentProps) => {
  const cardClassName = [radioCardClasses.card, className]
    .filter(Boolean)
    .join(" ")

  return (
    <MantineRadio.Card className={cardClassName} {...props}>
      <Group
        wrap="nowrap"
        align="flex-start"
        gap="var(--tfg-molecule-padding-x)"
      >
        <MantineRadio.Indicator color="primary" />
        <div>
          <Text component="div" className={radioCardClasses.label}>
            {label}
          </Text>
          {description ? (
            <Text component="div" className={radioCardClasses.description}>
              {description}
            </Text>
          ) : null}
          {children}
        </div>
      </Group>
    </MantineRadio.Card>
  )
}

export const Radio = Object.assign(RadioRoot, {
  Group: MantineRadio.Group,
  Card: RadioCard,
  Indicator: MantineRadio.Indicator,
})

export { radioCardClasses }
